import { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from '../../../ui/toast';
import {
  addToFolder,
  createFolder,
  deleteFolder,
  listFolders,
  removeFromFolder,
  updateFolder,
} from '../../../utils/api_service/candi_bank_folders';
import { CandiBankFolder } from '../../../utils/types/candi_bank_types';

const toastAt = { position: 'bottom-center' as const };

/** Folders grouped under their creator's name for the sidebar ("root folder by user name"). */
export interface FolderGroup {
  creatorId: string;
  creatorName: string;
  folders: CandiBankFolder[];
}

const errorText = (e: unknown, fallback: string) =>
  e instanceof Error && e.message ? e.message : fallback;

/**
 * Folder list, the folder being viewed, and every mutation. Membership changes call
 * `onMembersChanged` so the explorer can re-read its rows (chips and folder views move).
 */
export const useCandiBankFolders = (onMembersChanged: () => void) => {
  const [folders, setFolders] = useState<CandiBankFolder[]>([]);
  const [loading, setLoading] = useState(false);
  const [activeFolderId, setActiveFolderId] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      setFolders(await listFolders());
    } catch {
      toast.error('Failed to load folders', toastAt);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  // A folder deleted elsewhere (or by us) must not stay selected: its search would 404.
  useEffect(() => {
    if (activeFolderId && !loading && !folders.some((f) => f.id === activeFolderId)) {
      setActiveFolderId(null);
    }
  }, [folders, activeFolderId, loading]);

  const activeFolder = useMemo(
    () => folders.find((f) => f.id === activeFolderId) ?? null,
    [folders, activeFolderId],
  );

  const groups: FolderGroup[] = useMemo(() => {
    const byCreator = new Map<string, FolderGroup>();
    for (const folder of folders) {
      const key = folder.createdBy.id;
      if (!byCreator.has(key)) {
        byCreator.set(key, { creatorId: key, creatorName: folder.createdBy.name, folders: [] });
      }
      byCreator.get(key)!.folders.push(folder);
    }
    return Array.from(byCreator.values()).sort((a, b) =>
      a.creatorName.localeCompare(b.creatorName),
    );
  }, [folders]);

  /** Returns the new folder, or null after showing the error (e.g. duplicate name). */
  const create = async (name: string, jobOpeningId?: string | null) => {
    try {
      const folder = await createFolder(name, jobOpeningId);
      setFolders((prev) => [...prev, folder].sort((a, b) => a.name.localeCompare(b.name)));
      toast.success(`Folder "${folder.name}" created`, toastAt);
      return folder;
    } catch (e) {
      toast.error(errorText(e, 'Failed to create folder'), toastAt);
      return null;
    }
  };

  const update = async (folderId: string, changes: { name?: string; jobOpeningId?: string | null }) => {
    try {
      const folder = await updateFolder(folderId, changes);
      setFolders((prev) => prev.map((f) => (f.id === folderId ? folder : f)));
      onMembersChanged(); // chips carry the folder name
      return folder;
    } catch (e) {
      toast.error(errorText(e, 'Failed to update folder'), toastAt);
      return null;
    }
  };

  const remove = async (folder: CandiBankFolder) => {
    try {
      await deleteFolder(folder.id);
      setFolders((prev) => prev.filter((f) => f.id !== folder.id));
      toast.success(`Folder "${folder.name}" deleted`, toastAt);
      onMembersChanged();
      return true;
    } catch (e) {
      toast.error(errorText(e, 'Failed to delete folder'), toastAt);
      return false;
    }
  };

  const addCandidates = async (folder: CandiBankFolder, jobApplicantIds: string[]) => {
    if (jobApplicantIds.length === 0) return;
    try {
      const { added, skipped } = await addToFolder(folder.id, jobApplicantIds);
      if (added > 0) {
        toast.success(
          `Added ${added} candidate${added !== 1 ? 's' : ''} to "${folder.name}"` +
            (skipped ? ` (${skipped} already there)` : ''),
          toastAt,
        );
        setFolders((prev) =>
          prev.map((f) => (f.id === folder.id ? { ...f, candidateCount: f.candidateCount + added } : f)),
        );
        onMembersChanged();
      } else {
        toast.info(`Already in "${folder.name}"`, toastAt);
      }
    } catch (e) {
      toast.error(errorText(e, 'Failed to add to folder'), toastAt);
    }
  };

  const removeCandidates = async (folder: CandiBankFolder, emails: string[]) => {
    if (emails.length === 0) return;
    try {
      const { removed } = await removeFromFolder(folder.id, emails);
      toast.success(`Removed ${removed} from "${folder.name}"`, toastAt);
      setFolders((prev) =>
        prev.map((f) =>
          f.id === folder.id ? { ...f, candidateCount: Math.max(0, f.candidateCount - removed) } : f,
        ),
      );
      onMembersChanged();
    } catch (e) {
      toast.error(errorText(e, 'Failed to remove from folder'), toastAt);
    }
  };

  return {
    folders,
    groups,
    loading,
    activeFolder,
    activeFolderId,
    setActiveFolderId,
    refresh,
    create,
    update,
    remove,
    addCandidates,
    removeCandidates,
  };
};

export type CandiBankFoldersState = ReturnType<typeof useCandiBankFolders>;
